import React from "react";
import { useParams } from "react-router-dom";
import {
  fetchMerchantSettlementReport,
  getMerchantApiErrorMessage,
} from "../data/merchantApi";
import {
  formatMerchantPercent,
  formatMerchantTruncatedNumber,
  formatMerchantPaymentMethod,
  pickMerchantField,
  toMerchantNumber,
  toMerchantString,
} from "../data/merchantLegacyUtils";

const EMPTY_TEXT = "\u2014";

function hasReportValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function getReportString(record, keys, fallback) {
  return toMerchantString(
    pickMerchantField(record, keys),
    typeof fallback === "undefined" ? EMPTY_TEXT : fallback,
  ) || (typeof fallback === "undefined" ? EMPTY_TEXT : fallback);
}

function getReportItems(section) {
  return Array.isArray(section && section.items) ? section.items : [];
}

function formatReportDisplay(value) {
  return hasReportValue(value) ? toMerchantString(value) : EMPTY_TEXT;
}

function formatReportNumber(value) {
  if (!hasReportValue(value)) {
    return EMPTY_TEXT;
  }

  return toMerchantNumber(value).toLocaleString("en-SG");
}

function formatReportAmount(value) {
  if (!hasReportValue(value)) {
    return EMPTY_TEXT;
  }

  return formatMerchantTruncatedNumber(value, 2, EMPTY_TEXT);
}

function formatReportCurrency(value, currency) {
  if (!hasReportValue(value)) {
    return EMPTY_TEXT;
  }

  return formatReportDisplay(currency || "SGD") + " " + formatReportAmount(value);
}

function formatReportPercent(value) {
  if (!hasReportValue(value)) {
    return EMPTY_TEXT;
  }

  return formatMerchantPercent(value, 2, EMPTY_TEXT);
}

function formatReportDate(value) {
  const normalized = toMerchantString(value, "");
  const matchedDate = normalized.match(/\d{4}-\d{2}-\d{2}/);

  if (!normalized) {
    return EMPTY_TEXT;
  }

  if (matchedDate && matchedDate[0]) {
    return matchedDate[0];
  }

  return normalized;
}

function formatReportPeriod(start, end) {
  const startDate = formatReportDate(start);
  const endDate = formatReportDate(end);

  if (startDate === EMPTY_TEXT && endDate === EMPTY_TEXT) {
    return EMPTY_TEXT;
  }

  if (startDate === endDate) {
    return startDate;
  }

  return startDate + " - " + endDate;
}

function formatIssuedAt(value) {
  const normalized = toMerchantString(value, "");

  if (!normalized) {
    return EMPTY_TEXT;
  }

  const parsedDate = new Date(normalized);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalized;
  }

  return parsedDate.toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBankAccount(bank) {
  const account = getReportString(bank, ["bank_account_masked", "bank_account"], "");

  return account ? "Account " + account : EMPTY_TEXT;
}

function getCountryType(countryCode) {
  return toMerchantString(countryCode, "").toUpperCase() === "SG" ? "Local" : "Foreign";
}

function getDailyDay(dateValue) {
  const normalized = toMerchantString(dateValue, "");
  const parsedDate = new Date(normalized + "T00:00:00");

  if (!normalized || Number.isNaN(parsedDate.getTime())) {
    return EMPTY_TEXT;
  }

  return parsedDate.toLocaleString("en-SG", {
    weekday: "short",
  });
}

function renderStatusBadge(label, tone) {
  return (
    <span className={"merchant-settlement-report-badge " + tone}>
      <span className="merchant-settlement-report-badge-dot" />
      {label}
    </span>
  );
}

function renderEmptyRow(colSpan, label) {
  return (
    <tr>
      <td className="merchant-settlement-report-empty-cell" colSpan={colSpan}>
        {label}
      </td>
    </tr>
  );
}

function ReportSection({ title, subtitle, children }) {
  return (
    <section className="merchant-settlement-report-section">
      <div className="merchant-settlement-report-section-head">
        <div className="merchant-settlement-report-section-title">{title}</div>
        <div className="merchant-settlement-report-section-subtitle">{subtitle}</div>
      </div>
      {children}
    </section>
  );
}

function ReportTable({ children }) {
  return (
    <div className="merchant-settlement-report-table-wrap">
      <table className="merchant-settlement-report-table">{children}</table>
    </div>
  );
}

function MerchantSettlementReportDocument({ reportData }) {
  const header = reportData && reportData.header ? reportData.header : {};
  const summary = reportData && reportData.settlement_summary
    ? reportData.settlement_summary
    : {};
  const paymentBreakdown = reportData && reportData.payment_method_breakdown
    ? reportData.payment_method_breakdown
    : {};
  const countryDistribution = reportData && reportData.card_issuing_country_distribution
    ? reportData.card_issuing_country_distribution
    : {};
  const dailySchedule = reportData && reportData.daily_settlement_schedule
    ? reportData.daily_settlement_schedule
    : {};
  const outletSummary = reportData && reportData.outlet_performance_summary
    ? reportData.outlet_performance_summary
    : {};
  const refundSummary = reportData && reportData.refund_summary
    ? reportData.refund_summary
    : {};
  const finalPayout = reportData && reportData.final_net_payout_confirmation
    ? reportData.final_net_payout_confirmation
    : {};
  const bank = header.settlement_bank || {};
  const currency = getReportString(header, ["currency"], "SGD");
  const financialRows = Array.isArray(summary.financial_reconciliation)
    ? summary.financial_reconciliation
    : [];
  const paymentRows = getReportItems(paymentBreakdown);
  const countryRows = getReportItems(countryDistribution);
  const dailyRows = getReportItems(dailySchedule);
  const outletRows = getReportItems(outletSummary);
  const refundRows = getReportItems(refundSummary);
  const dailyTotals = dailySchedule.totals || {};
  const outletTotals = outletSummary.totals || {};
  const refundTotals = refundSummary.totals || {};
  const logoSrc = process.env.PUBLIC_URL + "/merchant-login-logo.webp";
  const periodLabel = getReportString(header, ["period_label"], "Settlement Period");
  const reportTitle = getReportString(header, ["report_title"], "Settlement Report");
  const reference = getReportString(header, ["reference"], EMPTY_TEXT);
  const issuedAt = formatIssuedAt(header.issued_at || finalPayout.report_generated_at);

  return (
    <React.Fragment>
      <header className="merchant-settlement-report-toolbar">
        <div className="merchant-settlement-report-toolbar-brand">
          <img src={logoSrc} alt="Uniweb Pte. Ltd." />
          <div>
            <div className="merchant-settlement-report-toolbar-name">Uniweb Pte. Ltd.</div>
            <div className="merchant-settlement-report-toolbar-subtitle">Merchant Portal</div>
          </div>
        </div>
        <div className="merchant-settlement-report-toolbar-spacer" />
        <div className="merchant-settlement-report-toolbar-meta">
          <span className="merchant-settlement-report-pill">{periodLabel}</span>
          <span className="merchant-settlement-report-pill">Settlements</span>
          <span className="merchant-settlement-report-ref">{reference}</span>
          <button
            className="merchant-settlement-report-print-button"
            type="button"
            onClick={function handlePrintReport() {
              window.print();
            }}
          >
            Save as PDF
          </button>
        </div>
      </header>

      <main className="merchant-settlement-report-page">
        <article className="merchant-settlement-report-card">
          <div className="merchant-settlement-report-header">
            <div className="merchant-settlement-report-header-left">
              <img src={logoSrc} alt="Uniweb Pte. Ltd." />
              <div>
                <div className="merchant-settlement-report-company">Uniweb Pte. Ltd.</div>
                <div className="merchant-settlement-report-license">
                  Licensed Payment Institution - MAS PS20200248
                </div>
              </div>
            </div>
            <div className="merchant-settlement-report-header-right">
              <div className="merchant-settlement-report-header-label">{reportTitle}</div>
              <div className="merchant-settlement-report-period">{periodLabel}</div>
              <div className="merchant-settlement-report-header-ref">
                Ref: {reference} - Issued {issuedAt}
              </div>
            </div>
          </div>

          <div className="merchant-settlement-report-strip">
            <div className="merchant-settlement-report-strip-cell">
              <div className="merchant-settlement-report-strip-label">Merchant</div>
              <div className="merchant-settlement-report-strip-value">
                {getReportString(header, ["merchant_name"], "Merchant")}
              </div>
              <div className="merchant-settlement-report-strip-sub">
                {getReportString(header, ["merchant_reference"], EMPTY_TEXT)}
                {getReportString(header, ["uen"], "") ? " - UEN " + header.uen : ""}
              </div>
            </div>
            <div className="merchant-settlement-report-strip-cell">
              <div className="merchant-settlement-report-strip-label">Settlement Bank</div>
              <div className="merchant-settlement-report-strip-value">
                {getReportString(bank, ["bank_name"], EMPTY_TEXT)}
              </div>
              <div className="merchant-settlement-report-strip-sub">{formatBankAccount(bank)}</div>
            </div>
            <div className="merchant-settlement-report-strip-cell">
              <div className="merchant-settlement-report-strip-label">Settlement Model</div>
              <div className="merchant-settlement-report-strip-value">
                {getReportString(header, ["settlement_model"], EMPTY_TEXT)}
              </div>
              <div className="merchant-settlement-report-strip-sub">
                Currency: {currency} - Rolling Reserve:{" "}
                {getReportString(header, ["rolling_reserve"], "Not Applicable")}
              </div>
            </div>
          </div>

          <div className="merchant-settlement-report-body">
            <ReportSection
              title="Settlement Summary"
              subtitle={"Period: " + formatReportPeriod(summary.period_start, summary.period_end)}
            >
              <div className="merchant-settlement-report-kpis">
                <div className="merchant-settlement-report-kpi">
                  <div className="merchant-settlement-report-kpi-bar brand" />
                  <div className="merchant-settlement-report-kpi-label">Total Gross Volume</div>
                  <div className="merchant-settlement-report-kpi-value">
                    {formatReportCurrency(summary.total_gross_volume, currency)}
                  </div>
                  <div className="merchant-settlement-report-kpi-sub">
                    {formatReportNumber(summary.total_transaction_count)} transactions
                  </div>
                </div>
                <div className="merchant-settlement-report-kpi">
                  <div className="merchant-settlement-report-kpi-bar red" />
                  <div className="merchant-settlement-report-kpi-label">Total Fees Deducted</div>
                  <div className="merchant-settlement-report-kpi-value">
                    {formatReportCurrency(summary.total_fees_deducted, currency)}
                  </div>
                  <div className="merchant-settlement-report-kpi-sub">
                    Blended MDR {formatReportPercent(summary.blended_mdr_pct)}
                  </div>
                </div>
                <div className="merchant-settlement-report-kpi">
                  <div className="merchant-settlement-report-kpi-bar green" />
                  <div className="merchant-settlement-report-kpi-label">Total Net Payout</div>
                  <div className="merchant-settlement-report-kpi-value green">
                    {formatReportCurrency(summary.total_net_payout, currency)}
                  </div>
                  <div className="merchant-settlement-report-kpi-sub">
                    Paid to {getReportString(bank, ["bank_name"], "settlement bank")}
                  </div>
                </div>
                <div className="merchant-settlement-report-kpi">
                  <div className="merchant-settlement-report-kpi-bar amber" />
                  <div className="merchant-settlement-report-kpi-label">Refunds Processed</div>
                  <div className="merchant-settlement-report-kpi-value">
                    {formatReportCurrency(summary.refunds_processed_amount, currency)}
                  </div>
                  <div className="merchant-settlement-report-kpi-sub">
                    {formatReportNumber(summary.refunds_processed_count)} transactions -{" "}
                    {formatReportPercent(summary.refunds_gmv_pct)} of GMV
                  </div>
                </div>
              </div>

              <ReportTable>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="right">Transactions</th>
                    <th className="right">Gross ({currency})</th>
                    <th className="right">Fees ({currency})</th>
                    <th className="right">Net ({currency})</th>
                  </tr>
                </thead>
                <tbody>
                  {financialRows.length
                    ? financialRows.map(function renderReconciliationRow(row, index) {
                      return (
                        <tr key={getReportString(row, ["category"], "row") + index}>
                          <td>{formatReportDisplay(row.category)}</td>
                          <td className="mono right">{formatReportNumber(row.txn_count)}</td>
                          <td className="mono right">{formatReportAmount(row.gross_amount)}</td>
                          <td className="mono right red">{formatReportAmount(row.fees_amount)}</td>
                          <td className="mono right green">{formatReportAmount(row.net_amount)}</td>
                        </tr>
                      );
                    })
                    : renderEmptyRow(5, "No financial reconciliation data.")}
                </tbody>
              </ReportTable>
            </ReportSection>

            <div className="merchant-settlement-report-divider" />

            <ReportSection
              title="Payment Method Breakdown"
              subtitle={
                "Settlement date: " + formatReportDate(paymentBreakdown.settlement_date)
              }
            >
              <ReportTable>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Method</th>
                    <th>Network / Issuer</th>
                    <th className="right">Transactions</th>
                    <th className="right">Gross ({currency})</th>
                    <th className="right">MDR</th>
                    <th className="right">Fees ({currency})</th>
                    <th className="right">Net ({currency})</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentRows.length
                    ? paymentRows.map(function renderPaymentRow(row, index) {
                      return (
                        <tr key={formatReportDisplay(row.payment_method) + index}>
                          <td>{formatReportDisplay(row.category)}</td>
                          <td>
                            <span className="merchant-settlement-report-dot" />
                            <strong>
                              {formatMerchantPaymentMethod(row.payment_method, "Payment")}
                            </strong>
                          </td>
                          <td className="dim">{formatReportDisplay(row.network)}</td>
                          <td className="mono right">{formatReportNumber(row.txn_count)}</td>
                          <td className="mono right">{formatReportAmount(row.gross_amount)}</td>
                          <td className="mono right">{formatReportPercent(row.mdr)}</td>
                          <td className="mono right red">{formatReportAmount(row.fees_amount)}</td>
                          <td className="mono right green">{formatReportAmount(row.net_amount)}</td>
                        </tr>
                      );
                    })
                    : renderEmptyRow(8, "No payment method breakdown data.")}
                </tbody>
              </ReportTable>
            </ReportSection>

            <ReportSection
              title="Card Issuing Country Distribution"
              subtitle={
                "Settlement date: " + formatReportDate(countryDistribution.settlement_date)
              }
            >
              <ReportTable>
                <thead>
                  <tr>
                    <th>Country</th>
                    <th className="center">ISO</th>
                    <th className="right">Transactions</th>
                    <th className="right">Gross ({currency})</th>
                    <th className="right">Share</th>
                    <th className="center">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {countryRows.length
                    ? countryRows.map(function renderCountryRow(row, index) {
                      const countryType = getCountryType(row.country_code);

                      return (
                        <tr key={formatReportDisplay(row.country_code) + index}>
                          <td>{formatReportDisplay(row.country_code)}</td>
                          <td className="mono center">
                            {formatReportDisplay(row.country_code).toUpperCase()}
                          </td>
                          <td className="mono right">{formatReportNumber(row.txn_count)}</td>
                          <td className="mono right">{formatReportAmount(row.gross_amount)}</td>
                          <td className="mono right">{formatReportPercent(row.share_pct)}</td>
                          <td className="center">
                            {renderStatusBadge(
                              countryType,
                              countryType === "Local" ? "ok" : "warn",
                            )}
                          </td>
                        </tr>
                      );
                    })
                    : renderEmptyRow(6, "No card issuing country data.")}
                </tbody>
              </ReportTable>
            </ReportSection>

            {dailyRows.length ? (
              <React.Fragment>
                <div className="merchant-settlement-report-divider" />

                <ReportSection
                  title="Daily Settlement Schedule"
                  subtitle={
                    "Period: " +
                    formatReportPeriod(dailySchedule.period_start, dailySchedule.period_end)
                  }
                >
                  <ReportTable>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Day</th>
                        <th className="right">Batches</th>
                        <th className="right">Transactions</th>
                        <th className="right">Gross ({currency})</th>
                        <th className="right">Fees ({currency})</th>
                        <th className="right">Net Payout ({currency})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyRows.map(function renderDailyRow(row, index) {
                        return (
                          <tr key={formatReportDisplay(row.date) + index}>
                            <td className="mono nowrap">{formatReportDate(row.date)}</td>
                            <td className="dim">{getDailyDay(row.date)}</td>
                            <td className="mono right">{formatReportNumber(row.batch_count)}</td>
                            <td className="mono right">{formatReportNumber(row.txn_count)}</td>
                            <td className="mono right">{formatReportAmount(row.gross_amount)}</td>
                            <td className="mono right red">{formatReportAmount(row.fees_amount)}</td>
                            <td className="mono right green">
                              {formatReportAmount(row.net_payout_amount)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="merchant-settlement-report-total-row">
                        <td>Total</td>
                        <td />
                        <td className="mono right">
                          {formatReportNumber(dailyTotals.batch_count)}
                        </td>
                        <td className="mono right">
                          {formatReportNumber(dailyTotals.txn_count)}
                        </td>
                        <td className="mono right">
                          {formatReportAmount(dailyTotals.gross_amount)}
                        </td>
                        <td className="mono right red">
                          {formatReportAmount(dailyTotals.fees_amount)}
                        </td>
                        <td className="mono right green">
                          {formatReportAmount(dailyTotals.net_payout_amount)}
                        </td>
                      </tr>
                    </tbody>
                  </ReportTable>
                </ReportSection>
              </React.Fragment>
            ) : null}

            <ReportSection
              title="Outlet Performance Summary"
              subtitle={"Period: " + formatReportPeriod(outletSummary.period_start, outletSummary.period_end)}
            >
              <ReportTable>
                <thead>
                  <tr>
                    <th className="center">#</th>
                    <th>Outlet</th>
                    <th>Location</th>
                    <th className="right">Transactions</th>
                    <th className="right">Gross ({currency})</th>
                    <th className="right">Share</th>
                    <th className="right">Fees ({currency})</th>
                    <th className="right">Net ({currency})</th>
                  </tr>
                </thead>
                <tbody>
                  {outletRows.length
                    ? outletRows.map(function renderOutletRow(row, index) {
                      return (
                        <tr key={formatReportDisplay(row.store_id) + index}>
                          <td className="mono center">{index + 1}</td>
                          <td>
                            <strong>{formatReportDisplay(row.store_name)}</strong>
                            <div className="merchant-settlement-report-small">
                              Store ID {formatReportDisplay(row.store_id)}
                            </div>
                          </td>
                          <td className="dim">{formatReportDisplay(row.location)}</td>
                          <td className="mono right">{formatReportNumber(row.txn_count)}</td>
                          <td className="mono right">{formatReportAmount(row.gross_amount)}</td>
                          <td className="mono right nowrap">
                            {formatReportPercent(row.share_pct)}
                          </td>
                          <td className="mono right red">{formatReportAmount(row.fees_amount)}</td>
                          <td className="mono right green">{formatReportAmount(row.net_amount)}</td>
                        </tr>
                      );
                    })
                    : renderEmptyRow(8, "No outlet performance data.")}
                  {outletRows.length ? (
                    <tr className="merchant-settlement-report-total-row">
                      <td>Total</td>
                      <td />
                      <td />
                      <td className="mono right">{formatReportNumber(outletTotals.txn_count)}</td>
                      <td className="mono right">{formatReportAmount(outletTotals.gross_amount)}</td>
                      <td />
                      <td className="mono right red">
                        {formatReportAmount(outletTotals.fees_amount)}
                      </td>
                      <td className="mono right green">
                        {formatReportAmount(outletTotals.net_amount)}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </ReportTable>
            </ReportSection>

            <ReportSection
              title="Refund Summary"
              subtitle={"Period: " + formatReportPeriod(refundSummary.period_start, refundSummary.period_end)}
            >
              <ReportTable>
                <thead>
                  <tr>
                    <th>Refund Type</th>
                    <th className="right">Transactions</th>
                    <th className="right">Amount ({currency})</th>
                    <th className="right">GMV Share</th>
                  </tr>
                </thead>
                <tbody>
                  {refundRows.length
                    ? refundRows.map(function renderRefundRow(row, index) {
                      return (
                        <tr key={formatReportDisplay(row.refund_type) + index}>
                          <td>{formatReportDisplay(row.refund_type)}</td>
                          <td className="mono right">{formatReportNumber(row.txn_count)}</td>
                          <td className="mono right red">{formatReportAmount(row.amount)}</td>
                          <td className="mono right">{formatReportPercent(row.gmv_pct)}</td>
                        </tr>
                      );
                    })
                    : renderEmptyRow(4, "No refund summary data.")}
                  {refundRows.length ? (
                    <tr className="merchant-settlement-report-total-row">
                      <td>Total</td>
                      <td className="mono right">{formatReportNumber(refundTotals.txn_count)}</td>
                      <td className="mono right red">{formatReportAmount(refundTotals.amount)}</td>
                      <td className="mono right">{formatReportPercent(refundTotals.gmv_pct)}</td>
                    </tr>
                  ) : null}
                </tbody>
              </ReportTable>
            </ReportSection>

            <ReportSection title="Final Net Payout Confirmation" subtitle="Settlement bank confirmation">
              <div className="merchant-settlement-report-payout">
                <div className="merchant-settlement-report-payout-left">
                  <div className="merchant-settlement-report-payout-label">Total Net Payout</div>
                  <div className="merchant-settlement-report-payout-amount">
                    {formatReportCurrency(finalPayout.total_net_payout, currency)}
                  </div>
                  <div className="merchant-settlement-report-payout-note">
                    Period {formatReportPeriod(finalPayout.period_start, finalPayout.period_end)}
                    {" - "}
                    {formatReportNumber(finalPayout.total_batches)} batches.
                  </div>
                </div>
                <div className="merchant-settlement-report-payout-right">
                  <div className="merchant-settlement-report-meta-row">
                    <span>Bank</span>
                    <strong>{getReportString(finalPayout, ["bank_name"], EMPTY_TEXT)}</strong>
                  </div>
                  <div className="merchant-settlement-report-meta-row">
                    <span>Bank Code</span>
                    <strong>{getReportString(finalPayout, ["bank_code"], EMPTY_TEXT)}</strong>
                  </div>
                  <div className="merchant-settlement-report-meta-row">
                    <span>Account</span>
                    <strong>
                      {getReportString(finalPayout, ["bank_account_masked"], EMPTY_TEXT)}
                    </strong>
                  </div>
                  <div className="merchant-settlement-report-meta-row">
                    <span>Account Holder</span>
                    <strong>
                      {getReportString(finalPayout, ["account_holder_name"], EMPTY_TEXT)}
                    </strong>
                  </div>
                  <div className="merchant-settlement-report-meta-row">
                    <span>Gross / Fees</span>
                    <strong>
                      {formatReportCurrency(finalPayout.gross_volume, currency)} /{" "}
                      {formatReportCurrency(finalPayout.total_fees, currency)}
                    </strong>
                  </div>
                  <div className="merchant-settlement-report-meta-row">
                    <span>Generated</span>
                    <strong>{formatIssuedAt(finalPayout.report_generated_at)}</strong>
                  </div>
                </div>
              </div>
            </ReportSection>

            <div className="merchant-settlement-report-disclaimer">
              <strong>Disclaimer:</strong> This report is generated from Uniweb merchant settlement
              records and is intended for merchant reconciliation. Please contact Uniweb support if
              any settlement value requires investigation.
            </div>
          </div>

          <footer className="merchant-settlement-report-footer">
            <div>
              Uniweb Pte. Ltd.
              <br />
              Merchant settlement report
            </div>
            <div>
              <span className="merchant-settlement-report-footer-mono">{reference}</span>
              <br />
              {issuedAt}
            </div>
          </footer>
        </article>
      </main>
    </React.Fragment>
  );
}

function MerchantSettlementReportState({ title, message, summaryId, isLoading, onRetry }) {
  return (
    <div className="merchant-settlement-report-state-shell">
      <div className="merchant-settlement-report-state-card">
        <img
          className="merchant-settlement-report-state-logo"
          src={process.env.PUBLIC_URL + "/merchant-login-logo.webp"}
          alt="Uniweb Pte. Ltd."
        />
        <div className="merchant-settlement-report-state-kicker">Settlement Report</div>
        <h1>{title}</h1>
        <p>{message}</p>
        <div className="merchant-settlement-report-state-ref">Summary ID: {summaryId}</div>
        {isLoading ? (
          <div className="merchant-inline-status merchant-inline-status-loading">
            <span className="merchant-request-spinner" />
            Request in progress
          </div>
        ) : null}
        {onRetry ? (
          <button
            className="merchant-settlement-report-print-button"
            type="button"
            onClick={onRetry}
          >
            Retry
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function MerchantSettlementReportPage() {
  const { summaryId } = useParams();
  const [requestState, setRequestState] = React.useState("loading");
  const [feedbackMessage, setFeedbackMessage] = React.useState("");
  const [reportData, setReportData] = React.useState(null);
  const [retryCount, setRetryCount] = React.useState(0);

  React.useEffect(
    function loadMerchantSettlementReport() {
      let isActive = true;

      async function requestReport() {
        if (!summaryId) {
          setRequestState("empty");
          setFeedbackMessage("Missing settlement summary ID.");
          setReportData(null);
          return;
        }

        setRequestState("loading");
        setFeedbackMessage("");

        try {
          const response = await fetchMerchantSettlementReport(summaryId);

          if (!isActive) {
            return;
          }

          if (!response || typeof response !== "object") {
            setRequestState("empty");
            setFeedbackMessage("The settlement report returned no data.");
            setReportData(null);
            return;
          }

          setReportData(response);
          setRequestState("success");
        } catch (error) {
          if (!isActive) {
            return;
          }

          setRequestState("error");
          setReportData(null);
          setFeedbackMessage(
            getMerchantApiErrorMessage(
              error,
              "Settlement report could not be loaded from the merchant API.",
            ),
          );
        }
      }

      requestReport();

      return function cleanupMerchantSettlementReport() {
        isActive = false;
      };
    },
    [summaryId, retryCount],
  );

  if (requestState === "loading") {
    return (
      <MerchantSettlementReportState
        title="Loading report"
        message="Fetching the settlement report from the merchant API."
        summaryId={summaryId}
        isLoading
      />
    );
  }

  if (requestState === "error") {
    return (
      <MerchantSettlementReportState
        title="Report unavailable"
        message={feedbackMessage}
        summaryId={summaryId}
        onRetry={function handleRetry() {
          setRetryCount(function incrementRetryCount(currentCount) {
            return currentCount + 1;
          });
        }}
      />
    );
  }

  if (requestState === "empty" || !reportData) {
    return (
      <MerchantSettlementReportState
        title="No report data"
        message={feedbackMessage || "The settlement report has no renderable data yet."}
        summaryId={summaryId}
      />
    );
  }

  return (
    <div className="merchant-settlement-report-shell">
      <MerchantSettlementReportDocument reportData={reportData} />
    </div>
  );
}
